/**
 * ConfirmDialog Component — Soorgaa Design System
 * 
 * Standard confirmation dialog for destructive or high-impact actions.
 * Built on top of the accessible Modal primitive.
 * 
 * Usage:
 * <ConfirmDialog
 *   isOpen={isDiscarding}
 *   title="Discard in-progress exam?"
 *   message="Your answers will be lost and cannot be recovered."
 *   variant="destructive"
 *   confirmText="Discard exam"
 *   onConfirm={handleDiscard}
 *   onCancel={() => setIsDiscarding(false)}
 * />
 */
import React from "react";
import { Modal } from "./Modal";
import { Button } from "./Button";
import { AlertTriangle, Info } from "lucide-react";

export interface ConfirmDialogProps {
  isOpen: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
  onClose?: () => void;
  title: string;
  message: React.ReactNode;
  confirmText?: string;
  confirmLabel?: string;
  cancelText?: string;
  cancelLabel?: string;
  variant?: "primary" | "destructive";
  loading?: boolean;
}

export function ConfirmDialog({
  isOpen,
  onConfirm,
  onCancel,
  onClose,
  title,
  message,
  confirmText,
  confirmLabel,
  cancelText,
  cancelLabel,
  variant = "primary",
  loading = false,
}: ConfirmDialogProps) {
  const isDestructive = variant === "destructive";
  const handleCancel = onCancel ?? onClose ?? (() => {});
  const resolvedConfirmText = confirmText ?? confirmLabel ?? "Confirm";
  const resolvedCancelText = cancelText ?? cancelLabel ?? "Cancel";

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      size="sm"
      closeOnBackdrop={!loading}
      closeOnEscape={!loading}
      showCloseButton={!loading}
    >
      <div className="flex flex-col items-center sm:items-start text-center sm:text-left space-y-4">
        <div
          className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${
            isDestructive
              ? "bg-destructive/10 text-destructive border border-destructive/20"
              : "bg-primary/10 text-primary border border-primary/20"
          }`}
          aria-hidden="true"
        >
          {isDestructive ? <AlertTriangle size={24} /> : <Info size={24} />}
        </div>

        <div className="space-y-1.5 w-full">
          <h3 className="text-base font-bold text-foreground">{title}</h3>
          <div className="text-sm text-muted-foreground leading-relaxed">{message}</div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row items-center justify-end gap-2.5 w-full pt-4 border-t border-border mt-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={loading}
            className="w-full sm:w-auto"
          >
            {resolvedCancelText}
          </Button>
          <Button
            type="button"
            variant={isDestructive ? "destructive" : "primary"}
            onClick={onConfirm}
            loading={loading}
            loadingText="Processing…"
            className="w-full sm:w-auto"
          >
            {resolvedConfirmText}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
